import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  DownloadOutlined,
  DollarCircleOutlined,
  FundOutlined,
  InfoCircleOutlined,
  PrinterOutlined,
  RedoOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Card, Col, Row, Table, Tag } from 'antd';
import {
  AButton,
  AInput,
  ASelect,
  AStatisticCard,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import { useCsvExport } from '../../../hooks/useCsvExport';
import { useToast } from '../../../hooks/toast';
import { IFinancial } from '../../../shared/interfaces';
import api from '../../../services/api';
import { currency } from '../../../shared/utils/functions';
import { formatDate } from '../../../shared/utils/format-date';
import { useNavigate } from 'react-router-dom';

interface ICashSummaryRow {
  key: string;
  method_payment: string;
  entries: number;
  exits: number;
  balance: number;
  quantity: number;
}

const getTodayRange = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  const value = `${year}-${month}-${day}`;

  return { dateI: value, dateF: value };
};

type TMovementTypeFilter = 'all' | 'entry' | 'exit';

const CashRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { exportCsv } = useCsvExport();
  const today = getTodayRange();

  const [movements, setMovements] = useState<IFinancial[]>([]);
  const [loading, setLoading] = useState(false);
  const [date_initial, setDate_initial] = useState<string | null>(today.dateI);
  const [date_final, setDate_final] = useState<string | null>(today.dateF);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TMovementTypeFilter>('all');
  const [methodFilter, setMethodFilter] = useState<string>('all');

  const loadMovements = useCallback(async () => {
    setLoading(true);
    setMovements([]);

    try {
      const response = await api.post('/financials/movement', {
        financial_plan: null,
        date_initial,
        date_final,
      });

      setMovements(response.data);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Não foi possível carregar o controle de caixa.',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, date_final, date_initial]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  const paymentMethodOptions = useMemo(() => {
    const methods = Array.from(
      new Set(
        movements
          .map((movement) => movement.method_payment || 'Não informado')
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return [
      { label: 'Todas as formas de pagamento', value: 'all' },
      ...methods.map((method) => ({ label: method, value: method })),
    ];
  }, [movements]);

  const filteredMovements = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return movements.filter((movement) => {
      const matchesType =
        typeFilter === 'all' ||
        (typeFilter === 'entry' && movement.type === 1) ||
        (typeFilter === 'exit' && movement.type === 0);

      const movementMethod = movement.method_payment || 'Não informado';
      const matchesMethod =
        methodFilter === 'all' || movementMethod === methodFilter;

      const haystack = [
        movement.client_name,
        movement.description,
        movement.method_payment,
        movement.number_doc,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch || haystack.includes(normalizedSearch);

      return matchesType && matchesMethod && matchesSearch;
    });
  }, [methodFilter, movements, search, typeFilter]);

  const totalEntries = useMemo(
    () =>
      filteredMovements
        .filter((item) => item.type === 1)
        .reduce((sum, item) => sum + Number(item.value || 0), 0),
    [filteredMovements],
  );

  const totalExits = useMemo(
    () =>
      filteredMovements
        .filter((item) => item.type === 0)
        .reduce((sum, item) => sum + Number(item.value || 0), 0),
    [filteredMovements],
  );

  const balance = totalEntries - totalExits;

  const summaryByMethod = useMemo<ICashSummaryRow[]>(() => {
    const grouped = new Map<string, ICashSummaryRow>();

    filteredMovements.forEach((movement) => {
      const method = movement.method_payment || 'Não informado';
      const current = grouped.get(method) || {
        key: method,
        method_payment: method,
        entries: 0,
        exits: 0,
        balance: 0,
        quantity: 0,
      };

      if (movement.type === 1) {
        current.entries += Number(movement.value || 0);
      } else {
        current.exits += Number(movement.value || 0);
      }

      current.balance = current.entries - current.exits;
      current.quantity += 1;

      grouped.set(method, current);
    });

    return Array.from(grouped.values()).sort((a, b) => b.balance - a.balance);
  }, [filteredMovements]);

  const featuredMethods = useMemo(
    () => summaryByMethod.slice(0, 4),
    [summaryByMethod],
  );

  const totalTransactions = filteredMovements.length;

  const exportMovements = useCallback(() => {
    exportCsv(
      filteredMovements,
      [
        'Tipo',
        'Data Pagamento',
        'Data Vencimento',
        'Cliente/Fornecedor',
        'Descricao',
        'Forma',
        'Valor',
      ],
      `caixa-${date_initial || 'inicio'}-${date_final || 'fim'}.csv`,
      (item: IFinancial) => [
        item.type === 1 ? 'Entrada' : 'Saida',
        item.payment_date ? formatDate(item.payment_date) : '',
        item.due_date ? formatDate(item.due_date) : '',
        item.client_name || '',
        item.description || '',
        item.method_payment || 'Nao informado',
        Number(item.value || 0)
          .toFixed(2)
          .replace('.', ','),
      ],
    );
  }, [date_final, date_initial, exportCsv, filteredMovements]);

  const printCashRegister = useCallback(() => {
    window.print();
  }, []);

  return (
    <PageLayout>
      <PageIntro
        badge="Financeiro"
        icon={<FundOutlined />}
        title="Controle de caixa"
        description="Acompanhe as movimentações efetivamente recebidas e pagas no período, com saldo operacional e resumo por forma de pagamento."
        actions={
          <Row gutter={[8, 8]}>
            <Col>
              <AButton icon={<DownloadOutlined />} onClick={exportMovements}>
                Exportar CSV
              </AButton>
            </Col>
            <Col>
              <AButton icon={<PrinterOutlined />} onClick={printCashRegister}>
                Imprimir
              </AButton>
            </Col>
            <Col>
              <AButton
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/financials')}
              >
                Voltar
              </AButton>
            </Col>
          </Row>
        }
      />

      <PagePanel>
        <Card>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={6}>
              <AInput
                type="date"
                prefix={<CalendarOutlined />}
                value={date_initial || ''}
                onChange={(e) => setDate_initial(e.target.value)}
              />
            </Col>
            <Col xs={24} md={6}>
              <AInput
                type="date"
                prefix={<CalendarOutlined />}
                value={date_final || ''}
                onChange={(e) => setDate_final(e.target.value)}
              />
            </Col>
            <Col xs={24} md={6}>
              <ASelect
                value={typeFilter}
                onChange={(value) =>
                  setTypeFilter((value as TMovementTypeFilter) || 'all')
                }
                options={[
                  { label: 'Todos os tipos', value: 'all' },
                  { label: 'Somente entradas', value: 'entry' },
                  { label: 'Somente saídas', value: 'exit' },
                ]}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} md={6}>
              <ASelect
                value={methodFilter}
                onChange={(value) =>
                  setMethodFilter((value as string) || 'all')
                }
                options={paymentMethodOptions}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} md={14}>
              <AInput
                placeholder="Buscar por cliente, descrição, documento ou forma"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col xs={24} md={10}>
              <Row gutter={[8, 8]} justify="end">
                <Col>
                  <AButton
                    onClick={() => {
                      const range = getTodayRange();
                      setDate_initial(range.dateI);
                      setDate_final(range.dateF);
                    }}
                  >
                    Hoje
                  </AButton>
                </Col>
                <Col>
                  <AButton icon={<RedoOutlined />} onClick={loadMovements}>
                    Atualizar
                  </AButton>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>
      </PagePanel>

      <PagePanel>
        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} lg={18}>
              <strong>
                Leitura operacional do caixa baseada apenas em lançamentos já
                pagos ou recebidos.
              </strong>
              <div style={{ color: '#64748b', marginTop: 6 }}>
                O filtro por operador ainda não aparece neste MVP porque esse
                relatório não traz o nome do usuário responsável pela baixa no
                payload atual do backend. As formas de pagamento exibidas abaixo
                são sempre as cadastradas e realmente usadas na clínica.
              </div>
            </Col>
            <Col xs={24} lg={6}>
              <Tag icon={<InfoCircleOutlined />} color="blue">
                MVP sem abertura/fechamento
              </Tag>
            </Col>
          </Row>
        </Card>
      </PagePanel>

      <PagePanel>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} xl={6}>
            <AStatisticCard
              title="Entradas"
              value={currency(totalEntries)}
              color="#3f8600"
              icon={<ArrowDownOutlined />}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <AStatisticCard
              title="Saídas"
              value={currency(totalExits)}
              color="#cf1322"
              icon={<ArrowUpOutlined />}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <AStatisticCard
              title="Saldo do Caixa"
              value={currency(balance)}
              color={balance >= 0 ? '#1677ff' : '#cf1322'}
              icon={<DollarCircleOutlined />}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <AStatisticCard
              title="Movimentações"
              value={totalTransactions}
              color="#000"
              icon={<CreditCardOutlined />}
            />
          </Col>
        </Row>
      </PagePanel>

      <PagePanel>
        <Row gutter={[16, 16]}>
          {featuredMethods.map((item) => (
            <Col xs={24} md={12} xl={6} key={item.key}>
              <AStatisticCard
                title={item.method_payment}
                value={currency(item.entries)}
                color={item.entries >= item.exits ? '#1677ff' : '#cf1322'}
                icon={<CreditCardOutlined />}
              />
            </Col>
          ))}
        </Row>
      </PagePanel>

      <PagePanel>
        <Card title="Resumo por forma de pagamento">
          <Table
            rowKey="key"
            size="small"
            loading={loading}
            dataSource={summaryByMethod}
            pagination={false}
            locale={{
              emptyText: 'Nenhuma movimentação encontrada no período.',
            }}
            columns={[
              {
                title: 'Forma de pagamento',
                dataIndex: 'method_payment',
              },
              {
                title: 'Lançamentos',
                dataIndex: 'quantity',
                width: 120,
              },
              {
                title: 'Entradas',
                dataIndex: 'entries',
                width: 140,
                render: (value: number) => currency(value),
              },
              {
                title: 'Saídas',
                dataIndex: 'exits',
                width: 140,
                render: (value: number) => currency(value),
              },
              {
                title: 'Saldo',
                dataIndex: 'balance',
                width: 140,
                render: (value: number) => (
                  <strong style={{ color: value >= 0 ? '#3f8600' : '#cf1322' }}>
                    {currency(value)}
                  </strong>
                ),
              },
            ]}
          />
        </Card>
      </PagePanel>

      <PagePanel>
        <Card title="Movimentações do caixa">
          <Table
            rowKey="id"
            loading={loading}
            dataSource={filteredMovements}
            pagination={false}
            scroll={{ x: 1100, y: 460 }}
            locale={{
              emptyText: 'Nenhuma movimentação encontrada no período.',
            }}
            columns={[
              {
                title: 'Tipo',
                dataIndex: 'type',
                width: 110,
                render: (value: number) => (
                  <Tag color={value === 1 ? 'green' : 'red'}>
                    {value === 1 ? 'Entrada' : 'Saída'}
                  </Tag>
                ),
              },
              {
                title: 'Pagamento',
                dataIndex: 'payment_date',
                width: 130,
                render: (value: string) => formatDate(value),
              },
              {
                title: 'Vencimento',
                dataIndex: 'due_date',
                width: 130,
                render: (value: string) => formatDate(value),
              },
              {
                title: 'Cliente / Fornecedor',
                dataIndex: 'client_name',
                width: 240,
              },
              {
                title: 'Descrição',
                dataIndex: 'description',
                width: 260,
              },
              {
                title: 'Forma',
                dataIndex: 'method_payment',
                width: 180,
                render: (value: string) => value || 'Não informado',
              },
              {
                title: 'Valor',
                dataIndex: 'value',
                width: 130,
                render: (value: number, record: IFinancial) => (
                  <strong
                    style={{ color: record.type === 1 ? '#3f8600' : '#cf1322' }}
                  >
                    {currency(value)}
                  </strong>
                ),
              },
            ]}
          />
        </Card>
      </PagePanel>
    </PageLayout>
  );
};

export default CashRegisterPage;
